import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export type RequestType = 'CREATE' | 'CHANGE' | 'TERMINATE';

export type ChecklistResult = {
  checklistId: string;
  label: string;
  sortOrder: number;
  requestType: RequestType;
  isDeleted: boolean;
};

export type ListChecklistParams = {
  requestType?: RequestType;
  includeDeleted?: boolean;
};

export type CreateChecklistParams = {
  label: string;
  requestType: RequestType;
  sortOrder: number;
};

export type UpdateChecklistParams = {
  checklistId: string;
  label: string;
};

export type DeleteChecklistParams = {
  checklistId: string;
};

export type ReorderChecklistItem = {
  checklistId: string;
  sortOrder: number;
};

export type ReorderChecklistParams = {
  items: ReorderChecklistItem[];
};

// --- API関数 ---

/**
 * チェックリスト一覧取得API
 * @param params.requestType - 申請種別（任意, 最大20文字, enum: CREATE/CHANGE/TERMINATE）
 * @param params.includeDeleted - 削除済みを含む（任意, boolean, 省略時false）
 */
export const listChecklist = async (params: ListChecklistParams): Promise<ChecklistResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_CONTRACT_REQUEST_CHECKLIST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * チェックリスト登録API
 * @param params.label - ラベル（必須, 最大200文字）
 * @param params.requestType - 申請種別（必須, 最大20文字, enum: CREATE/CHANGE/TERMINATE）
 * @param params.sortOrder - 表示順（必須, 0以上）
 */
export const createChecklist = async (params: CreateChecklistParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_CONTRACT_REQUEST_CHECKLIST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.checklistId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * チェックリスト編集API
 * @param params.checklistId - チェックリストID（必須, 最大64文字）
 * @param params.label - ラベル（必須, 最大200文字）
 */
export const updateChecklist = async (params: UpdateChecklistParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_CONTRACT_REQUEST_CHECKLIST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * チェックリスト削除API
 * @param params.checklistId - チェックリストID（必須, 最大64文字）
 */
export const deleteChecklist = async (params: DeleteChecklistParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_CONTRACT_REQUEST_CHECKLIST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * チェックリスト並び替えAPI
 * @param params.items - 並び替え対象の一覧（必須）
 * @param params.items[].checklistId - チェックリストID（必須, 最大64文字）
 * @param params.items[].sortOrder - 表示順（必須, 0以上）
 */
export const reorderChecklist = async (params: ReorderChecklistParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.REORDER_CONTRACT_REQUEST_CHECKLIST, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
